import { ReactNode, useCallback, useMemo } from 'react';
import { HostContext, HostCtx, ConfirmOptions, ToastLevel } from '@admin-portal/host-contract';
import { showConfirm } from './components/ConfirmModal';
import { showToast } from './components/Toast';
import type { AdminSession } from './auth/useSession';
import { withAppBasePath } from './nav';

export function HostProvider({ session, children }: { session: AdminSession; children: ReactNode }) {
  const fetchApi = useCallback(async (path: string, init?: RequestInit) => {
    const url = path.startsWith('/') ? `/api${path}` : `/api/${path}`;
    return fetch(url, {
      ...init,
      credentials: 'include',
    });
  }, []);

  const signOut = useCallback(() => {
    window.location.href = `/api/auth/line/logout?next=${encodeURIComponent(withAppBasePath('/'))}`;
  }, []);

  const refreshUser = useCallback(async () => {
    await session.refresh();
  }, [session]);

  const toast = useCallback((level: ToastLevel, msg: string) => {
    showToast(level, msg);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return showConfirm(opts);
  }, []);

  const ctx: HostCtx = useMemo(
    () => ({
      user: session.user,
      fetchApi,
      signOut,
      refreshUser,
      toast,
      confirm,
    }),
    [session.user, fetchApi, signOut, refreshUser, toast, confirm]
  );

  return <HostContext.Provider value={ctx}>{children}</HostContext.Provider>;
}
